import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import type { GiftCardBalance as GiftCardBalanceSchema } from '@amplience/content-types/typings/c-gift-card-balance';
import { yupResolver } from '@hookform/resolvers/yup';
import { fromCents } from '@shared/utils/src/price/money';
import { giftCardBalanceSchema } from '@shared/validation/src/storefront';
import { BlackButton } from '@storefront/lib-global/baseComponents/button';
import { FieldContainer } from '@storefront/lib-global/baseComponents/containers/FieldContainer';
import { ErrorMessage } from '@storefront/lib-global/baseComponents/errors';
import { useGetGiftCardBalance } from '@storefront/lib-global/hooks/useGetGiftCardBalance';
import { useLoadingCursor } from '@storefront/lib-global/hooks/useLoadingCursor';
import { Redeem } from '@storefront/lib-global/icons';
import { TypographyStyles, colours, maxWidthPartial, media, spacing } from '@storefront/lib-global/stylings';
import { getCurrencySymbol } from '@storefront/lib-global/utils/currencyHelper';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';
import { GiftCardBalanceInput } from './GiftCardBalanceInput';

const S = {
  Container: styled.div`
    ${maxWidthPartial({ maxWidth: 1440, withPadding: true })}
    margin: ${spacing.L} auto;

    @media ${media.greaterThan('lg')} {
      margin: 80px auto;
    }
  `,
  GiftCardBalanceContainer: styled.form`
    display: flex;
    flex-direction: column;
    gap: ${spacing.XS};
    margin-top: ${spacing.L};
    width: 100%;

    @media ${media.greaterThan('lg')} {
      flex-direction: row;
      margin: 0 0 ${spacing.XS};

      & > div {
        width: 50%;
      }
    }
  `,
  Title: styled.h2`
    color: ${colours.BLACK};
    margin: 0;
    text-transform: uppercase;
    ${TypographyStyles.Headings.H2}

    @media ${media.greaterThan('lg')} {
      width: 100%;
    }
  `,
  BodyText: styled.p`
    color: ${colours.BLACK};
    margin: ${spacing.XS} 0 0;
    ${TypographyStyles.Body.Small.Regular}

    @media ${media.greaterThan('lg')} {
      margin-top: 0;
      width: 333px;
    }
  `,
  GiftCardBodyTextAndBalanceContainer: styled.div`
    @media ${media.greaterThan('lg')} {
      display: flex;
      gap: 111px;
      margin-top: 60px;
    }
  `,
  Button: styled(BlackButton)`
    align-items: center;
    display: flex;
    gap: ${spacing.XXXS};
    height: 44px;
    justify-content: center;
    margin-bottom: 0;
    width: 100%;

    & path {
      fill: ${colours.WHITE};
    }

    @media ${media.greaterThan('lg')} {
      height: 50px;
      width: 50%;
    }
  `,
  ResultsContainer: styled.div`
    align-items: center;
    background-color: ${colours.LIGHT_BLUE};
    display: flex;
    gap: 20px;
    height: 96px;
    margin-top: ${spacing.XS};
    padding: ${spacing.S};
    width: 100%;

    & path {
      fill: ${colours.BLACK};
    }
  `,
  ResultsUl: styled.ul`
    display: flex;
    flex-direction: column;
    gap: ${spacing.XXXS};
    margin: 0;
    padding: 0;
    ${TypographyStyles.Body.Small.Regular}

    & li span:last-child {
      ${TypographyStyles.Body.Small.SemiBold}
    }

    @media ${media.greaterThan('lg')} {
      flex-direction: row;
    }
  `,
  CardAndBalanceContainer: styled.div`
    display: flex;
    flex-direction: column;

    @media ${media.greaterThan('lg')} {
      flex-direction: row;
      gap: 20px;
    }
  `,
  GiftCardAndResultsContainer: styled.div`
    width: 100%;
  `,
  ResultIconContainer: styled.div`
    height: 20px;
    width: 20px;
  `,
};

export type CheckGiftCardBalanceProps = GiftCardBalanceSchema;

type FormValues = {
  giftCardNumber: number;
};

export function CheckGiftCardBalance({ bodyText, headerTitle }: CheckGiftCardBalanceProps) {
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors: formError, isSubmitting, isValid },
  } = useForm<FormValues>({ mode: 'onChange', resolver: yupResolver(giftCardBalanceSchema) });
  const { t } = useTranslation(['lib-products-common']);
  const [cardNumber, setCardNumber] = useState<number | null>(null);

  const [giftCardNumber] = useWatch({ control, name: ['giftCardNumber'] });

  const { submitGiftCard, loading, balance } = useGetGiftCardBalance(giftCardNumber, setCardNumber, setError);

  useLoadingCursor(loading);

  return (
    <S.Container>
      <S.Title>{getLocalizedValue(headerTitle) ?? t('giftcard.fallback.title')}</S.Title>
      <S.GiftCardBodyTextAndBalanceContainer>
        <S.BodyText>{getLocalizedValue(bodyText) ?? t('giftcard.fallback.body.text')}</S.BodyText>
        <S.GiftCardAndResultsContainer>
          <S.GiftCardBalanceContainer onSubmit={handleSubmit(submitGiftCard)}>
            <FieldContainer>
              <GiftCardBalanceInput
                aria-label={t('enter.giftcard.number')}
                placeholder={t('enter.giftcard.number')}
                type="number"
                aria-invalid={!!formError.giftCardNumber}
                aria-describedby="giftCard-invalid"
                testId="checkGiftCardBalance"
                isEmpty={!giftCardNumber}
                isInputError={!!formError.giftCardNumber}
                register={register('giftCardNumber')}
                isGiftCardBalance
              />
              <ErrorMessage id="giftCard-invalid" {...formError?.giftCardNumber}>
                {t(formError.giftCardNumber?.message as string)}
              </ErrorMessage>
            </FieldContainer>
            <S.Button type="submit" disabled={!isValid || isSubmitting}>
              <Redeem />
              {t('check.giftcard.balance')}
            </S.Button>
          </S.GiftCardBalanceContainer>
          {balance && (
            <S.ResultsContainer role="status">
              <S.ResultIconContainer>
                <Redeem />
              </S.ResultIconContainer>
              <S.CardAndBalanceContainer>
                <S.ResultsUl>
                  <li>
                    <span>{t('giftcard.card.number')} </span>
                    <span>{cardNumber}</span>
                  </li>
                  <li>
                    <span>{t('giftcard.remaining.balance')} </span>
                    <span>
                      {getCurrencySymbol(balance.currency)}
                      {fromCents(balance.value)}
                    </span>
                  </li>
                </S.ResultsUl>
              </S.CardAndBalanceContainer>
            </S.ResultsContainer>
          )}
        </S.GiftCardAndResultsContainer>
      </S.GiftCardBodyTextAndBalanceContainer>
    </S.Container>
  );
}
